import { useEffect, useRef, useState, useCallback } from "react";
import { Volume2, VolumeX, Flag } from "lucide-react";
import { gamesApi } from "@/lib/api";
import ArcadeShell from "@/components/games/ArcadeShell";
import { sfx, isMuted, setMuted } from "@/lib/candySfx";

const ROWS = 8;
const COLS = 8;
const TYPES = 6;
const SIZE = 40; // px per cell → 320px board, phone-friendly
const SWAP_MS = 170;
const POP_MS = 200;
const FALL_MS = 230;

// Bright, glossy candy palette (generic gems, not laundry-themed). Each type also has a distinct
// inner shape (clip-path) so colours are distinguishable for colour-blind players.
const CANDY = [
  { bg: "radial-gradient(circle at 32% 28%, #FF93A8, #FF4D6D 70%)", shape: "circle" },
  { bg: "radial-gradient(circle at 32% 28%, #FFC078, #FF9F1C 70%)", shape: "diamond" },
  { bg: "radial-gradient(circle at 32% 28%, #FFE98A, #FFC300 72%)", shape: "star" },
  { bg: "radial-gradient(circle at 32% 28%, #86E07A, #38B000 72%)", shape: "triangle" },
  { bg: "radial-gradient(circle at 32% 28%, #8FB8FF, #3A86FF 72%)", shape: "square" },
  { bg: "radial-gradient(circle at 32% 28%, #D59BF0, #9D4EDD 72%)", shape: "hex" },
];
const CLIP = {
  diamond: "polygon(50% 0, 100% 50%, 50% 100%, 0 50%)",
  star: "polygon(50% 0, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
  triangle: "polygon(50% 8%, 95% 92%, 5% 92%)",
  hex: "polygon(25% 5%, 75% 5%, 100% 50%, 75% 95%, 25% 95%, 0 50%)",
};

const delay = (ms) => new Promise((r) => setTimeout(r, ms));
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
const adjacent = (a, b) => a && b && Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1;
const rnd = () => Math.floor(Math.random() * TYPES);
const shuffle = (arr) => { for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; } };

function CandyBoard({ submitScore }) {
  const B = useRef([]); // 2D array of {id,type,_spawn} | null
  const idRef = useRef(1);
  const busyRef = useRef(false);
  const aliveRef = useRef(true);
  const scoreRef = useRef(0);
  const selRef = useRef(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const containerRef = useRef(null);

  const [tiles, setTiles] = useState([]);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [combo, setCombo] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [muted, setMutedState] = useState(isMuted());

  const mk = (type) => ({ id: idRef.current++, type, _spawn: false });

  const paint = useCallback((clearing) => {
    const list = [];
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      const t = B.current[r]?.[c];
      if (t) list.push({ id: t.id, type: t.type, r, c, spawn: t._spawn, clearing: !!clearing && clearing.has(t.id) });
    }
    setTiles(list);
  }, []);

  const swapCells = (r, c, nr, nc) => { const t = B.current[r][c]; B.current[r][c] = B.current[nr][nc]; B.current[nr][nc] = t; };

  const findMatches = () => {
    const cells = new Set();
    const runs = [];
    const b = B.current;
    for (let r = 0; r < ROWS; r++) {
      let run = 1;
      for (let c = 1; c <= COLS; c++) {
        const same = c < COLS && b[r][c] && b[r][c - 1] && b[r][c].type === b[r][c - 1].type;
        if (same) run++;
        else { if (run >= 3) { runs.push(run); for (let k = c - run; k < c; k++) cells.add(r + "," + k); } run = 1; }
      }
    }
    for (let c = 0; c < COLS; c++) {
      let run = 1;
      for (let r = 1; r <= ROWS; r++) {
        const same = r < ROWS && b[r][c] && b[r - 1][c] && b[r][c].type === b[r - 1][c].type;
        if (same) run++;
        else { if (run >= 3) { runs.push(run); for (let k = r - run; k < r; k++) cells.add(k + "," + c); } run = 1; }
      }
    }
    return { cells, runs };
  };

  const hasMove = () => {
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      for (const [dr, dc] of [[0, 1], [1, 0]]) {
        const nr = r + dr, nc = c + dc;
        if (nr >= ROWS || nc >= COLS) continue;
        swapCells(r, c, nr, nc);
        const m = findMatches().cells.size > 0;
        swapCells(r, c, nr, nc);
        if (m) return true;
      }
    }
    return false;
  };

  const collapse = () => {
    const spawned = [];
    for (let c = 0; c < COLS; c++) {
      const col = [];
      for (let r = ROWS - 1; r >= 0; r--) if (B.current[r][c]) col.push(B.current[r][c]);
      for (let r = ROWS - 1; r >= 0; r--) {
        if (col.length) B.current[r][c] = col.shift();
        else { const t = mk(rnd()); t._spawn = true; B.current[r][c] = t; spawned.push(t); }
      }
    }
    return spawned;
  };

  const doReshuffle = async () => {
    sfx.reshuffle();
    const types = [];
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) types.push(B.current[r][c].type);
    let tries = 0;
    do {
      shuffle(types);
      let i = 0;
      for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) B.current[r][c].type = types[i++];
      tries++;
    } while ((findMatches().cells.size > 0 || !hasMove()) && tries < 80);
    paint();
    await delay(FALL_MS);
  };

  const resolve = async () => {
    let chain = 0;
    while (aliveRef.current) {
      const { cells, runs } = findMatches();
      if (!cells.size) break;
      chain++;
      sfx.match(chain);
      let step = 0;
      for (const len of runs) { const f = len >= 5 ? 2 : len >= 4 ? 1.5 : 1; step += Math.round(len * 10 * f); }
      step = Math.round(step * (1 + (chain - 1) * 0.5));
      scoreRef.current += step;
      setScore(scoreRef.current);
      if (chain > 1) { setCombo({ text: `Combo x${chain}! +${step}`, key: Date.now() }); }
      const clearing = new Set();
      for (const k of cells) { const [r, c] = k.split(",").map(Number); clearing.add(B.current[r][c].id); }
      paint(clearing);
      await delay(POP_MS);
      if (!aliveRef.current) return;
      for (const k of cells) { const [r, c] = k.split(",").map(Number); B.current[r][c] = null; }
      const spawned = collapse();
      paint();
      await nextFrame();
      spawned.forEach((t) => { t._spawn = false; });
      paint();
      await delay(FALL_MS);
    }
    if (aliveRef.current && !hasMove()) await doReshuffle();
  };

  const trySwap = async (a, b) => {
    if (busyRef.current || !adjacent(a, b)) return;
    busyRef.current = true;
    swapCells(a.r, a.c, b.r, b.c);
    sfx.swap();
    paint();
    await delay(SWAP_MS);
    if (findMatches().cells.size > 0) {
      await resolve();
    } else {
      swapCells(a.r, a.c, b.r, b.c);
      sfx.invalid();
      paint();
      await delay(SWAP_MS);
    }
    busyRef.current = false;
  };

  const start = useCallback(() => {
    busyRef.current = false;
    scoreRef.current = 0;
    setScore(0);
    setCombo(null);
    setGameOver(false);
    selRef.current = null;
    setSelected(null);
    const b = [];
    for (let r = 0; r < ROWS; r++) {
      b.push([]);
      for (let c = 0; c < COLS; c++) {
        let type;
        do { type = rnd(); } while (
          (c >= 2 && b[r][c - 1]?.type === type && b[r][c - 2]?.type === type) ||
          (r >= 2 && b[r - 1][c]?.type === type && b[r - 2][c]?.type === type)
        );
        b[r].push({ id: idRef.current++, type, _spawn: false });
      }
    }
    B.current = b;
    if (!hasMove()) doReshuffle();
    else paint();
  }, [paint]);

  useEffect(() => {
    aliveRef.current = true;
    start();
    return () => { aliveRef.current = false; };
  }, [start]);

  const cellFromEvent = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const c = Math.floor((e.clientX - rect.left) / SIZE);
    const r = Math.floor((e.clientY - rect.top) / SIZE);
    if (r < 0 || r >= ROWS || c < 0 || c >= COLS) return null;
    return { r, c };
  };
  const select = (cell) => { selRef.current = cell; setSelected(cell); };

  const onPointerDown = (e) => {
    if (busyRef.current || gameOver) return;
    const cell = cellFromEvent(e);
    if (!cell) return;
    dragRef.current = cell;
    movedRef.current = false;
    containerRef.current?.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (busyRef.current || !dragRef.current) return;
    const cell = cellFromEvent(e);
    if (cell && adjacent(dragRef.current, cell)) {
      const from = dragRef.current;
      dragRef.current = null;
      movedRef.current = true;
      select(null);
      trySwap(from, cell);
    }
  };
  const onPointerUp = () => {
    if (movedRef.current) { dragRef.current = null; return; }
    const cell = dragRef.current;
    dragRef.current = null;
    if (!cell) return;
    const sel = selRef.current;
    if (sel && adjacent(sel, cell)) { select(null); trySwap(sel, cell); }
    else if (sel && sel.r === cell.r && sel.c === cell.c) select(null);
    else select(cell);
  };

  const finishRun = () => {
    if (gameOver) return;
    busyRef.current = true;
    sfx.finish();
    const banked = Math.min(scoreRef.current, 100000);
    setFinalScore(scoreRef.current);
    submitScore(banked);
    setGameOver(true);
  };

  const toggleMute = () => { const n = !muted; setMuted(n); setMutedState(n); };

  const BOARD_W = COLS * SIZE;
  const BOARD_H = ROWS * SIZE;

  return (
    <div className="flex w-full flex-col items-center gap-3 select-none">
      <div className="flex w-full max-w-[320px] items-center justify-between">
        <div><p className="font-display text-xl font-bold" data-testid="candy-score">{score}</p><p className="text-[11px] text-muted-foreground">Score</p></div>
        <div className="flex items-center gap-2">
          <button onClick={finishRun} data-testid="candy-finish" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground"><Flag className="h-4 w-4" /> Finish</button>
          <button onClick={toggleMute} data-testid="candy-mute" aria-label={muted ? "Unmute" : "Mute"} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card">
            {muted ? <VolumeX className="h-4 w-4 text-muted-foreground" /> : <Volume2 className="h-4 w-4 text-primary" />}
          </button>
        </div>
      </div>

      <div className="relative rounded-2xl p-1.5" style={{ background: "linear-gradient(160deg, #5B3A8A, #3A2460)" }}>
        <div
          ref={containerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => { dragRef.current = null; }}
          data-testid="candy-board"
          className="relative rounded-xl"
          style={{ width: BOARD_W, height: BOARD_H, touchAction: "none", background: "rgba(0,0,0,0.18)" }}
        >
          {selected ? (
            <div className="pointer-events-none absolute rounded-xl ring-4 ring-white/80" style={{ left: selected.c * SIZE + 2, top: selected.r * SIZE + 2, width: SIZE - 4, height: SIZE - 4 }} />
          ) : null}
          {tiles.map((t) => (
            <div
              key={t.id}
              data-testid={`candy-tile-${t.r}-${t.c}`}
              style={{
                position: "absolute",
                left: t.c * SIZE + 3,
                top: t.r * SIZE + 3,
                width: SIZE - 6,
                height: SIZE - 6,
                borderRadius: t.type === 4 ? 10 : 14,
                background: CANDY[t.type].bg,
                boxShadow: "inset 0 3px 5px rgba(255,255,255,0.55), inset 0 -4px 6px rgba(0,0,0,0.28), 0 2px 3px rgba(0,0,0,0.25)",
                transition: "top .23s cubic-bezier(.34,1.26,.64,1), left .17s ease, transform .2s ease, opacity .2s ease",
                transform: t.clearing ? "scale(0) rotate(40deg)" : t.spawn ? "scale(0.2)" : "scale(1)",
                opacity: t.clearing || t.spawn ? 0 : 1,
              }}
            >
              <div style={{ position: "absolute", inset: "26%", background: "rgba(255,255,255,0.7)", borderRadius: CANDY[t.type].shape === "circle" ? "50%" : 3, clipPath: CLIP[CANDY[t.type].shape] || "none" }} />
            </div>
          ))}
          {combo ? (
            <div key={combo.key} data-testid="candy-combo" className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/90 px-4 py-1.5 font-display text-sm font-bold text-primary shadow-lg" style={{ animation: "candyPop 0.7s ease forwards" }}>
              {combo.text}
            </div>
          ) : null}
          {gameOver ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-xl bg-black/60 p-6 text-center" data-testid="candy-result">
              <p className="font-display text-2xl font-bold text-white">Sweet run! 🍬</p>
              <p className="text-lg font-bold text-white">Score: {finalScore}</p>
              <button onClick={start} data-testid="candy-play-again" className="mt-1 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Play again</button>
            </div>
          ) : null}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Swap neighbours to line up 3+ — tap two or drag. Tap Finish to bank points.</p>
      <style>{`@keyframes candyPop{0%{transform:translate(-50%,-50%) scale(0.4);opacity:0}30%{transform:translate(-50%,-50%) scale(1.1);opacity:1}100%{transform:translate(-50%,-140%) scale(1);opacity:0}}`}</style>
    </div>
  );
}

export default function CandyMatch() {
  return (
    <ArcadeShell title="Candy Match" game="candy" stateKey="candyState" stateFn={gamesApi.candyState} unlockFn={gamesApi.candyUnlock} scoreFn={gamesApi.candyScore}>
      {({ submitScore }) => <CandyBoard submitScore={submitScore} />}
    </ArcadeShell>
  );
}
